import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgIf } from '@angular/common';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import Swal from 'sweetalert2';

import { TicketDTO } from 'src/app/DTO/TicketDTO';
import { TicketWaitingListInfoDTO } from 'src/app/DTO/tickets/TicketWaitingListInfoDTO';
import { ButtonLoadingComponent } from 'src/app/components/button-loading/button-loading.component';
import { ApiService } from 'src/app/services/api.service';
import { CommunityService } from 'src/app/services/community/community.service';
import { CustomFieldPhoneComponent } from 'src/app/components/custom-field-input/fields/components/phone/phone.component';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input-gg';

@Component({
  selector: 'app-waiting-list-ticket',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FloatLabelModule,
    InputTextModule,
    DropdownModule,
    TranslateModule,
    NgIf,
    ButtonLoadingComponent,
    CustomFieldPhoneComponent,
    NgxIntlTelInputModule
  ],
  templateUrl: './waiting-list-ticket.component.html',
  styleUrl: './waiting-list-ticket.component.scss'
})
export class WaitingListTicketComponent {

  readonly ticket = signal<TicketDTO | null>(null);
  readonly ticketPriceId = signal<number | null>(null);
  readonly maxTickets = signal<number>(10);
  public color = '#4e7dee';
  readonly form = signal<FormGroup | null>(null);
  readonly priceOptions = computed(() => {
    const t = this.ticket();
    if (!t?.prices?.length) return [];
    return t.prices.filter(p => p.isActive !== false);
  });
  readonly hasMultiplePrices = computed(() => (this.priceOptions().length ?? 0) > 1);

  /** True when the max label "(max: X)" should be shown; only when admin defined MaxByUser or MaxSalesPurchase. */
  get showMaxTicketsLabel(): boolean {
    const t = this.ticket();
    return (t?.maxByUser != null && t.maxByUser > 0) ||
           (t?.maxSalesPurchase != null && t.maxSalesPurchase > 0);
  }

  private readonly config = inject(DynamicDialogConfig);
  private readonly apiService = inject(ApiService);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly communityService = inject(CommunityService);

  public communityName = '';

  ngOnInit(): void {
    this.ticket.set(this.config.data?.ticket ?? null);
    this.ticketPriceId.set(this.config.data?.ticketPriceId ?? null);
    this.communityName = this.config.data?.communityName ?? '';
    this.communityService.Get().subscribe(x => { this.color = x.color1; });
    this.initForm();
    this.initPriceBehaviour();
    this.updateMaxTickets();
  }

  private initForm(): void {
    const f = this.fb.group({
      nom: ['', Validators.required],
      cognom: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      telefon: ['', Validators.required],
      persones: [1, [Validators.required, Validators.min(1)]],
      price: [null as number | null]
    });
    this.form.set(f);
  }

  private initPriceBehaviour(): void {
    const t = this.ticket();
    const f = this.form();
    const forcedPriceId = this.ticketPriceId();
    if (!f || !t) return;

    const priceCtrl = f.get('price');
    const hasMultiple = (t.prices?.length ?? 0) > 1;

    if (hasMultiple) {
      priceCtrl?.setValidators(Validators.required);
    } else {
      priceCtrl?.clearValidators();
      const firstPrice = t.prices?.[0];
      if (firstPrice?.id != null) {
        priceCtrl?.setValue(firstPrice.id);
      }
    }
    priceCtrl?.updateValueAndValidity({ emitEvent: false });

    if (forcedPriceId != null) {
      const selected = t.prices?.find(p => p.id === forcedPriceId);
      if (selected) {
        priceCtrl?.setValue(selected.id);
        priceCtrl?.disable({ emitEvent: false });
      }
    }
  }

  private updateMaxTickets(): void {
    const t = this.ticket();
    // Preferir el valor que envía la API. Si no viene, calcular igual que la API: MaxByUser ?? MaxSalesPurchase ?? 10
    let max = 10;
    if (t) {
      if (t.maxAllowedForWaitingList != null && t.maxAllowedForWaitingList > 0) {
        max = t.maxAllowedForWaitingList;
      } else if (t.maxByUser != null && t.maxByUser > 0) {
        max = t.maxByUser;
      } else if (t.maxSalesPurchase != null && t.maxSalesPurchase > 0) {
        max = t.maxSalesPurchase;
      } else {
        max = 10;
      }
    }
    this.maxTickets.set(max);
    const f = this.form();
    if (f) {
      const ctrl = f.get('persones');
      ctrl?.setValidators([Validators.required, Validators.min(1), Validators.max(this.maxTickets())]);
      ctrl?.updateValueAndValidity({ emitEvent: false });
    }
  }

  onSubmit = (): Promise<'success' | 'cancel'> => {
    return new Promise(resolve => {
      const f = this.form();
      if (!f || f.invalid) {
        f?.markAllAsTouched();
        return resolve('cancel');
      }
      const dto = this.buildDto(f);
      this.apiService.postTicketWaitingList(this.communityName, dto).subscribe({
        next: () => {
          this.handleSuccess();
          resolve('success');
        },
        error: err => {
          this.handleError(err);
          resolve('cancel');
        }
      });
    });
  };

  private buildDto(form: FormGroup): TicketWaitingListInfoDTO {
    const t = this.ticket();
    const priceId = form.value.price ?? this.ticketPriceId() ?? t?.prices?.[0]?.id;
    const dto = new TicketWaitingListInfoDTO();
    dto.ticketId = t!.id;
    dto.name = form.value.nom;
    dto.surname = form.value.cognom;
    dto.email = form.value.email;
    dto.phone = form.value.telefon;
    dto.people = form.value.persones;
    dto.ticketPriceId = priceId ?? undefined;
    return dto;
  }

  private handleSuccess(): void {
    Swal.fire({
      title: this.translate.instant('INSCRIPTIONS.WaitingList.SuccessTitle'),
      text: this.translate.instant('INSCRIPTIONS.WaitingList.SuccessSubtitle'),
      icon: 'success',
      confirmButtonText: this.translate.instant('Accept'),
      customClass: { confirmButton: 'swal-confirm-corp' },
    }).then(() => this.dialogRef.close());
  }

  private handleError(err: any): void {
    const body = this.getErrorBody(err);
    const code = String(body?.code ?? body?.Code ?? '');

    // These codes are already shown by ErrorHttpInterceptor / HttpErrorDisplayService.
    if (code === 'EMAIL_ALREADY_EXISTS' || this.isWaitingListMaxExceeded(err, body, code)) {
      this.dialogRef.close();
      return;
    }

    Swal.fire({
      icon: 'error',
      title: this.translate.instant('Oops'),
      text: body?.message ?? body?.detail ?? body?.Detail
        ?? this.translate.instant('GENERIC.error'),
      confirmButtonText: this.translate.instant('Accept'),
      customClass: { confirmButton: 'swal-confirm-corp' },
    });
  }

  private isWaitingListMaxExceeded(err: any, body: any, code: string): boolean {
    if (code === 'WAITING_LIST_MAX_EXCEEDED') {
      return true;
    }
    if (err?.status === 400 && body
      && (body.alreadyRequested != null || body.AlreadyRequested != null)
      && (body.maxAllowed != null || body.MaxAllowed != null)
      && (body.requestedNow != null || body.RequestedNow != null)) {
      return true;
    }
    const raw = typeof err?.error === 'string' ? err.error : JSON.stringify(body ?? '');
    return raw.includes('WAITING_LIST_MAX_EXCEEDED');
  }

  private getErrorBody(err: any): any {
    const body = err?.error;
    if (body == null) {
      return null;
    }
    if (typeof body === 'string') {
      try {
        return JSON.parse(body);
      } catch {
        return { message: body };
      }
    }
    return body;
  }
}
